import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Textarea } from 'components/common';
import {
    useActiveLanguagesQuery,
    useAdminContactQuery,
    useCreateContactMutation,
    useUpdateContactMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const emptyContact = {
    officeName: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    sortOrder: 0,
    isActive: true,
    translation: [],
};

const ContactForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState(emptyContact);

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: contactDetail, error: contactError } = useAdminContactQuery(id);
    const createContactMutation = useCreateContactMutation();
    const updateContactMutation = useUpdateContactMutation();
    const loading = createContactMutation.isPending || updateContactMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, title: '', slug: '', description: '' })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (contactDetail) setFormData(contactDetail);
    }, [contactDetail]);

    useEffect(() => {
        if (contactError) toast.error(contactError.message || 'Tải dữ liệu thất bại');
    }, [contactError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        try {
            const payload = { ...formData, sortOrder: Number(formData.sortOrder) };
            const res = id
                ? await updateContactMutation.mutateAsync({ id, data: payload })
                : await createContactMutation.mutateAsync(payload);

            if (res?.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/contacts');
            } else {
                if (res?.messageError) setErrors(res.messageError);
                else toast.error(res?.message || 'Lưu thất bại');
            }
        } catch (err) {
            toast.error(err.message || 'Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader
                title={id ? 'Cập nhật Thông tin Liên hệ' : 'Thêm Thông tin Liên hệ'}
                subtitle="Quản lý chi tiết liên hệ của công ty hiển thị trên trang web"
            />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Chi tiết Liên hệ</p>
                <div className="form-row">
                    <Input
                        label="Tên Văn phòng *"
                        name="officeName"
                        value={formData.officeName}
                        onChange={handleChange}
                        required
                        placeholder="VD: Trụ sở chính"
                    />
                    <Input
                        label="Địa chỉ *"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        placeholder="VD: 123 Đường ABC..."
                    />
                </div>
                <div className="form-row">
                    <Input
                        label="Điện thoại"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        error={errors.phone}
                        placeholder="VD: +84 24 1234 5678"
                    />
                    <Input
                        label="Fax"
                        name="fax"
                        value={formData.fax}
                        onChange={handleChange}
                        error={errors.fax}
                    />
                </div>
                <div className="form-row">
                    <Input
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={errors.email}
                        placeholder="VD: info@example.com"
                    />
                    <Input
                        label="Thứ tự sắp xếp"
                        name="sortOrder"
                        type="number"
                        value={formData.sortOrder}
                        onChange={handleChange}
                    />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button
                                        key={lang.id}
                                        type="button"
                                        className={`tab-btn ${activeTab === i ? 'active' : ''}`}
                                        onClick={() => setActiveTab(i)}
                                    >
                                        {lang.name} ({lang.code})
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(item => item.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input
                                            label="Tiêu đề"
                                            value={t.title || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)}
                                        />
                                        <Input
                                            label="Slug"
                                            value={t.slug || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)}
                                        />
                                        <Textarea
                                            label="Mô tả / Địa chỉ"
                                            value={t.description || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'description', e.target.value)}
                                            rows={4}
                                            placeholder="Địa chỉ văn phòng, giờ làm việc, v.v."
                                        />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}

                <div className="form-actions">
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/contacts')}>
                        Hủy
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default ContactForm;
